import {
  Box,
  CollapsibleCard,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  CloseCircleOutlineIcon,
  ContentSaveIcon,
  CreditCardIcon,
  EyeIcon,
  StamperIcon,
} from '@atgfr8/icons';
import { Button, SelectInput, ToastType } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import {
  CreditRequestPayload,
  CreditRequestResponse,
  customerCreditRequest,
  customerDetailsStore,
  setCustomerDetailsStore,
} from '@store/customers/customerDetails';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatAmount, handleToast } from '@utils/utils';
import { DateTime } from 'luxon';
import { createEffect, createSignal, Index, Show } from 'solid-js';

import { creditRequest as classes } from './classes';

const GeneralCreditRequest = () => {
  const [comments, setComments] = createSignal<{ [key: number]: string }>({});
  const [loading, setLoading] = createSignal(false);
  const [selectedStatuses, setSelectedStatuses] = createSignal<{
    [key: number]: string;
  }>({});
  const [enteredAmounts, setEnteredAmounts] = createSignal<{
    [key: number]: string;
  }>({});

  const handleCommentChange = (id: number, value: string) => {
    setComments((prev) => ({ ...prev, [id]: value }));
  };

  const handleStatusChange = (id: number, value: string) => {
    setSelectedStatuses((prev) => ({ ...prev, [id]: value }));
  };
  const handleAmountChange = (id: number, value: string) => {
    setEnteredAmounts((prev) => ({ ...prev, [id]: value }));
  };

  createEffect(() => {
    const requests = customerDetailsStore.customer.pendingCreditRequests;
    if (requests.length > 0 && Object.keys(selectedStatuses()).length === 0) {
      const defaultStatuses: { [key: number]: string } = {};
      for (const req of requests) {
        defaultStatuses[req.requestId] = 'Approve';
      }
      setSelectedStatuses(defaultStatuses);
    }
  });

  const handleCreditRequest = async (
    id: number,
    response: CreditRequestResponse,
    approvedAmount?: string,
  ) => {
    try {
      setLoading(true);
      const comment = comments()[id];
      const requestData: CreditRequestPayload = {
        id,
        response: response.replace(' ', '') as CreditRequestResponse,
        approvedAmount:
          approvedAmount != null ? Number(approvedAmount) : undefined,
      };
      if (comment) {
        requestData.comment = comment;
      }
      const res = await customerCreditRequest(requestData);
      if (res && res.isSuccess) {
        setComments((prev) => ({ ...prev, [id]: '' }));
        setCustomerDetailsStore(
          'customer',
          'pendingCreditRequests',
          customerDetailsStore.customer.pendingCreditRequests.filter(
            (req) => req.requestId !== id,
          ),
        );

        const message = {
          InReview: 'Request Resolved manually',
          Approve: 'Credit Increase Approved',
          Deny: 'Credit Increase Denied',
          PartialApproval: 'Credit Increase Partially Approved',
          Prepay: 'Credit Increase set to Prepay',
        };

        handleToast(
          ToastType.Success,
          message[requestData.response as keyof typeof message],
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const bgColor = {
    'Partial Approval': '#689F38',
    Approve: '#248670',
    Prepay: '#FF8F00',
    Deny: '#B00020',
    'In Review': '#123B50',
  };

  const iconMap = {
    'Partial Approval': <StamperIcon />,
    Approve: <StamperIcon />,
    Prepay: <CreditCardIcon />,
    Deny: <CloseCircleOutlineIcon />,
    'In Review': <EyeIcon />,
  };

  return (
    <>
      <Show when={loading()}>
        <AtgLoader />
      </Show>
      <Index each={customerDetailsStore.customer.pendingCreditRequests}>
        {(rowData) => {
          return (
            <CollapsibleCard startTitle="Credit Request" class="mb-2">
              <Box class="flex items-center justify-between">
                <Typography
                  variant="body1"
                  component="span"
                  sxProps={{ fontWeight: '500' }}
                >
                  {rowData().requestedByUserName}
                </Typography>
                <Typography variant="body1" component="span">
                  {DateTime.fromISO(rowData().requestDate).toFormat(
                    'MM/dd/yyyy',
                  )}
                </Typography>
              </Box>
              <Box class="flex items-center gap-2">
                <Typography variant="body1" component="span">
                  {/* {`${rowData().type} for`} */}
                  Credit Increase Request
                </Typography>
                <Typography
                  variant="body1"
                  component="span"
                  sxProps={classes.creditTypo}
                >
                  {formatAmount(rowData().amount)}
                </Typography>
              </Box>
              <Box class="flex items-center gap-2 mb-[10px]">
                <Typography variant="body1" component="span">
                  {rowData().details}
                </Typography>
              </Box>
              <Box display="flex" gap={2} mb={1.5} alignItems="center">
                <Box class="flex items-center gap-2">
                  {
                    iconMap[
                      selectedStatuses()[
                        rowData().requestId
                      ] as keyof typeof iconMap
                    ]
                  }
                  <SelectInput
                    label=""
                    value={selectedStatuses()[rowData().requestId] || 'Approve'}
                    menuItems={[
                      {
                        label: 'Partial Approval',
                        value: 'Partial Approval',
                      },
                      {
                        label: 'Approve',
                        value: 'Approve',
                      },
                      {
                        label: 'Prepay',
                        value: 'Prepay',
                      },
                      {
                        label: 'Deny',
                        value: 'Deny',
                      },
                      {
                        label: 'In Review',
                        value: 'In Review',
                      },
                    ]}
                    onChange={(val) =>
                      handleStatusChange(rowData().requestId, val)
                    }
                    placeholder="Select Status"
                    style={{
                      width: 'fit-content',
                      'max-width': 'fit-content',
                      'background-color':
                        bgColor[
                          selectedStatuses()[
                            rowData().requestId
                          ] as keyof typeof bgColor
                        ],
                      color: '#fff',
                      'border-color':
                        bgColor[
                          selectedStatuses()[
                            rowData().requestId
                          ] as keyof typeof bgColor
                        ],
                    }}
                    dataTestId={formatDataTestId(
                      'Credit request status',
                      'selectInput',
                    )}
                  />
                </Box>
                <Show
                  when={
                    selectedStatuses()[rowData().requestId] ===
                      'Partial Approval' ||
                    selectedStatuses()[rowData().requestId] === 'Approve'
                  }
                >
                  <TextField
                    class="w-[200px]!"
                    label="Credit Amount Approved"
                    type="number"
                    placeholder="Enter Amount"
                    value={enteredAmounts()[rowData().requestId] || ''}
                    onChange={(value: string) =>
                      handleAmountChange(rowData().requestId, value)
                    }
                    dataTestId={`general-credit-request-approved-amount-textInput-${rowData().requestId}`}
                  />
                </Show>
              </Box>
              <TextField
                multiline
                label="Credit Comments"
                rows={3}
                placeholder="Add a comment"
                value={comments()[rowData().requestId] || ''}
                onChange={(value) =>
                  handleCommentChange(rowData().requestId, value)
                }
                maxLength={1000}
                dataTestId={`general-credit-request-comments-textAreaField-${rowData().requestId}`}
              />
              <Box class="flex items-center justify-end mt-[15px]">
                <Button
                  startIcon={<ContentSaveIcon size={20} />}
                  variant="contained"
                  label={'Submit'}
                  onClick={() =>
                    handleCreditRequest(
                      rowData().requestId,
                      selectedStatuses()[
                        rowData().requestId
                      ] as CreditRequestResponse,
                      enteredAmounts()[rowData().requestId],
                    )
                  }
                  dataTestId={formatDataTestId('Submit', 'button')}
                />
              </Box>
            </CollapsibleCard>
          );
        }}
      </Index>
    </>
  );
};

export default GeneralCreditRequest;
